export interface EncryptedData {
  data: string;
  nonce: string;
}

export interface DecryptionConfig {
  enabled: boolean;
  masterKey?: string;
  salt?: string;
  info?: string;
  jwtToken?: string;
}

export class ChekinDecryption {
  algorithm = 'AES-GCM';
  keyLength = 256;

  async deriveKey(masterKey: string, salt: string, info = 'api-response-encryption') {
    const encoder = new TextEncoder();

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(masterKey),
      'HKDF',
      false,
      ['deriveKey'],
    );

    const saltBytes = encoder.encode(salt);
    const infoBytes = encoder.encode(info);

    return await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: saltBytes,
        info: infoBytes,
      },
      keyMaterial,
      {name: this.algorithm, length: this.keyLength},
      false,
      ['decrypt'],
    );
  }

  async decrypt(encryptedData: EncryptedData, key: CryptoKey) {
    const ciphertext = this.base64ToArrayBuffer(encryptedData?.data);
    const nonce = this.base64ToArrayBuffer(encryptedData?.nonce);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: this.algorithm,
        iv: nonce,
      },
      key,
      ciphertext,
    );

    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decrypted);
    return JSON.parse(jsonString);
  }

  base64ToArrayBuffer(base64: string) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

export class DecryptionManager {
  private decryption = new ChekinDecryption();
  private config: DecryptionConfig;
  private decryptionKey: CryptoKey | null = null;

  constructor(config: DecryptionConfig) {
    this.config = {
      masterKey: config.masterKey,
      salt: config.salt,
      info: config.info || 'api-response-encryption',
      jwtToken: config.jwtToken || undefined,
      ...config,
    };
  }

  async initialize(): Promise<CryptoKey | null> {
    if (!this.config.enabled) return null;

    if (!this.config.masterKey) {
      throw new Error(
        'Decryption requires masterKey (should match ENCRYPTION_MASTER_KEY setting)',
      );
    }

    if (!this.config.salt) {
      throw new Error('Decryption requires salt (should match ENCRYPTION_SALT setting)');
    }

    const info = this.config.jwtToken || this.config.info!;

    const decryptionKey = await this.decryption.deriveKey(
      this.config.masterKey,
      this.config.salt,
      info,
    );
    this.decryptionKey = decryptionKey;

    return decryptionKey;
  }

  setJwtToken(jwtToken: string): void {
    this.config.jwtToken = jwtToken;
    this.decryptionKey = null;
  }

  getEncryptionHeaders(): Record<string, string> {
    if (!this.config.enabled) return {};

    return {
      'X-Session-Type': 'basic',
    };
  }

  isResponseEncrypted(response: Response): boolean {
    return response.headers.get('X-Encrypted') === 'true';
  }

  async decryptResponse<T>(encryptedData: EncryptedData, key?: CryptoKey): Promise<T> {
    let decryptionKey = key || this.decryptionKey;
    if (!decryptionKey) {
      decryptionKey = await this.initialize();
    }

    return await this.decryption.decrypt(encryptedData, decryptionKey!);
  }

  async getDecryptionKey(): Promise<CryptoKey> {
    if (!this.decryptionKey) {
      await this.initialize();
    }
    return this.decryptionKey!;
  }

  isEnabled(): boolean {
    return this.config.enabled;
  }
}
